import currentTimeZone from '../config/timeZone';

type MonthDataBase = { monthName: string };
type DateDataBase = { date: string };

/**
 * Fills missing months in the last 12 months and sets missing numeric fields to 0.
 * Adds a `label` field formatted as `Month Year` (e.g., "Jan 2024").
 */
export function fillMissingMonthsGeneric<T extends MonthDataBase>(
  data: T[],
): (Omit<T, 'monthName'> & { label: string })[] {
  const today = new Date();
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const past12Months = [];
  for (let index = 0; index < 12; index += 1) {
    const date = new Date(today.getFullYear(), today.getMonth() - index, 1);
    const label = months[date.getMonth()];
    const year = date.getFullYear();
    past12Months.push({ key: label, fullLabel: `${label} ${year.toString().slice(2, 4)}` }); // Year sliced as 2 characters ( i.e: 2025 -> 25 )
  }

  const dataMap = new Map<string, T>();
  data.forEach((item) => dataMap.set(item.monthName, item));

  // Get all unique keys (except monthName) to fill missing fields
  const fieldKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'monthName') {
        fieldKeys.add(key);
      }
    });
  });

  const result = past12Months.map(({ key, fullLabel }) => {
    const existing = dataMap.get(key);
    if (existing) {
      const { monthName, ...rest } = existing;
      return { ...rest, label: fullLabel };
    }

    const filled: Record<string, any> = { label: fullLabel };
    fieldKeys.forEach((k) => {
      filled[k] = 0;
    });

    return filled as Omit<T, 'monthName'> & { label: string };
  });

  return result.reverse();
}

/**
 * Fills missing days in the past 30 days, formatting the label as "MMM DD" (e.g., "May 08"),
 * and filling missing numeric fields with 0.
 * Assumes input `label` is an ISO date string.
 */
export function fillMissingLastNDaysGeneric<T extends DateDataBase>(
  data: T[],
  nDays: number = 30,
): (Omit<T, 'date'> & { label: string })[] {
  const today = new Date();
  const past30Days: Date[] = [];

  for (let index = 0; index < nDays; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    past30Days.push(date);
  }

  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });

  // Lookup map for quick access
  const dataMap = new Map<string, T>();
  data.forEach((item) => {
    const iso = item.date.split('T')[0];
    dataMap.set(iso, item);
  });

  // Collect all keys except 'date'
  const fieldKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'date') fieldKeys.add(key);
    });
  });

  const result = past30Days.map((date) => {
    const isoDate = date.toISOString().split('T')[0];
    const existing = dataMap.get(isoDate);

    if (existing) {
      const { date: discardedDate, ...rest } = existing;
      return { ...rest, label: dateFormatter.format(date) };
    }

    const defaultItem: Record<string, any> = { label: dateFormatter.format(date) };
    fieldKeys.forEach((key) => {
      defaultItem[key] = 0;
    });

    return defaultItem as Omit<T, 'date'> & { label: string };
  });

  return result.reverse();
}

/**
 * Fills missing data for the past 24 hours with default values (0), grouped by hour and date.
 * @param data Input data containing at least `date` (YYYY-MM-DD) and `hour` (0–23)
 * @param timeZone Optional time zone (defaults to browser's currentTimeZone)
 * @returns Array of data objects, each with `label`, `hour`, and all other fields (defaulted to 0 if missing)
 */
export function fillMissingLast24HrsGeneric<T extends { date: string; hour: number | string }>(
  data: T[],
  timeZone: string = currentTimeZone,
): Array<Omit<T, 'date' | 'hour'> & { label: string }> {
  const now = new Date();
  const past24Hours: { date: string; hour: number }[] = [];

  for (let index = 0; index < 24; index += 1) {
    const d = new Date(now);
    d.setHours(now.getHours() - index, 0, 0, 0);

    const dateString = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);

    past24Hours.push({ date: dateString, hour: d.getHours() });
  }

  const dataMap = new Map(data.map((item) => [`${item.date}_${item.hour}`, item]));

  const allKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'date' && key !== 'hour') allKeys.add(key);
    });
  });

  const result = past24Hours.map(({ date, hour }) => {
    const key = `${date}_${hour}`;
    const existing = dataMap.get(key);

    if (existing) {
      const { date: unusedDate, hour: unusedHour, ...rest } = existing;
      return { label: hour.toString(), ...rest } as any;
    }

    const fallback: Record<string, any> = { label: hour.toString() };
    allKeys.forEach((k) => {
      fallback[k] = 0;
    });

    return fallback;
  });

  return result.reverse();
}
